import moment from 'moment/moment';

import { AMOUNTS } from './grids/filters/options/amount';
import { APPROVAL_TYPES } from './grids/filters/options/approval-type';
import { CREATE_DATES } from './grids/filters/options/create-date';
import { FULFILLMENT_STATUSES } from './grids/filters/options/fulfillmentStatuses';
import { REFUND_METHODS } from './grids/filters/options/refund-method';
import { RETURN_EXCHANGE_TYPE } from './grids/filters/options/return-exchange-type';
import { STATUS } from './grids/filters/options/status';
import { dateFilterOperators } from '../../../containers/base-filter-bar-container/reference-data-criteria-arguments';
import { calculateTargetDatesFromOperator } from '../../../services/filtering/api-param-map-service';
import { OptionFormTypes } from '../../../services/filtering/filter-form-render-service/option-form-types';

/**
 * Mapping Conventions for Enhanced Returns Filters
 *
 * These mappings transform filter values from their display form (camelCase) to their API form (snake_case).
 * This approach ensures consistency between the frontend and backend, while maintaining readable code.
 *
 * For example:
 * - 'returnExchangeTypes' maps 'return' to 'ret', 'exchange' to 'exc', etc.
 * - 'approvalType' maps 'eventBased' to 'event_based'
 * - 'paymentType' maps 'giftCard' to 'gift_card'
 * - etc.
 */

const ENHANCED_RETURNS = 'enhanced_returns';

// Mapping for return exchange types to use shortened values in API calls
export const RETURN_EXCHANGE_TYPES_MAPPING = {
  return: 'ret',
  exchange: 'exc',
  purchase: 'pch',
};

// Mapping for approval types to use proper values in API calls
const APPROVAL_TYPE_MAPPING = {
  manual: 'manual',
  eventBased: 'event_based',
};

// Mapping for payment types to use proper values in API calls
const PAYMENT_TYPE_MAPPING = {
  giftCard: 'gift_card',
  originalPaymentMethod: 'original_payment_method',
};

// Mapping for fulfillment statuses to use proper values in API calls
const FULFILLMENT_STATUSES_MAPPING = {
  delivered: 'delivered',
  inTransit: 'in_transit',
  preTransit: 'pre_transit',
};

export const mapEnhancedReturnsFiltersToRequestBody = (filters) => {
  return filters.reduce((body, filter) => {
    const { filterValue, criterionArgumentValue, criterionOperatorValue, type } = filter;

    // Mirrors date filter logic in other api-param mappers
    if (filterValue === CREATE_DATES && type === OptionFormTypes.TYPE_DATE_COMPARISON_SHIPMENTS) {
      if (criterionOperatorValue === dateFilterOperators.dateCustomRange) {
        return {
          ...body,
          minDate: moment(criterionArgumentValue.startRangeDate).format(),
          maxDate: moment(criterionArgumentValue.endRangeDate).add(1, 'days').format(),
        };
      }
      if (criterionOperatorValue === dateFilterOperators.dateOnOrAfter) {
        return {
          ...body,
          minDate: moment(criterionArgumentValue).format(),
        };
      }
      if (criterionOperatorValue === dateFilterOperators.dateOnOrBefore) {
        return {
          ...body,
          maxDate: moment(criterionArgumentValue).add(1, 'days').format(),
        };
      }
      // For other date operators (today, yesterday, last week, etc.)
      const targetDates = calculateTargetDatesFromOperator(criterionOperatorValue, ENHANCED_RETURNS);
      return {
        ...body,
        minDate:
          typeof targetDates.greaterThanOrEqualToDate === 'string'
            ? moment(targetDates.greaterThanOrEqualToDate).format()
            : targetDates.greaterThanOrEqualToDate.format(),
        maxDate:
          typeof targetDates.lessThanDate === 'string' ? moment(targetDates.lessThanDate).format() : targetDates.lessThanDate.format(),
      };
    }

    // Special handling for amount filters
    if (filterValue === AMOUNTS && type === OptionFormTypes.TYPE_CURRENCY_COMPARISON) {
      // Handle different numeric operators
      // Helper function to ensure value is a valid C# double
      const ensureDouble = (value) => {
        // If value is an array, take the first element
        const rawValue = Array.isArray(value) ? value[0] : value;
        // Parse to float and ensure it's a valid C# double with 2 decimal places
        const floatValue = parseFloat(rawValue);
        // Return a number with 2 decimal places precision (not a string)
        return Number.isNaN(floatValue) ? 0.0 : parseFloat(floatValue.toFixed(2));
      };

      if (criterionOperatorValue === 'Gt') {
        // For "greater than", add 0.01 to the input amount
        const minAmount = parseFloat(ensureDouble(criterionArgumentValue)) + 0.01;
        return {
          ...body,
          minAmount: parseFloat(minAmount.toFixed(2)),
        };
      }
      if (criterionOperatorValue === 'Gte') {
        // For "greater than or equal to", use the exact input amount
        return {
          ...body,
          minAmount: ensureDouble(criterionArgumentValue),
        };
      }
      if (criterionOperatorValue === 'Lt') {
        // For "less than", subtract 0.01 from the input amount
        const maxAmount = parseFloat(ensureDouble(criterionArgumentValue)) - 0.01;
        return {
          ...body,
          maxAmount: parseFloat(maxAmount.toFixed(2)),
        };
      }
      if (criterionOperatorValue === 'Lte') {
        // For "less than or equal to", use the exact input amount
        return {
          ...body,
          maxAmount: ensureDouble(criterionArgumentValue),
        };
      }
      if (criterionOperatorValue === 'Eq') {
        // For "equal to", we set both min and max to the same value
        const value = ensureDouble(criterionArgumentValue);
        return {
          ...body,
          minAmount: value,
          maxAmount: value,
        };
      }
    }

    // Special case for returnExchangeTypes
    if (filterValue === RETURN_EXCHANGE_TYPE) {
      const mappedValues = Array.isArray(criterionArgumentValue)
        ? criterionArgumentValue.map((value) => RETURN_EXCHANGE_TYPES_MAPPING[value] || value)
        : [RETURN_EXCHANGE_TYPES_MAPPING[criterionArgumentValue] || criterionArgumentValue];
      const existingValues = body[RETURN_EXCHANGE_TYPE] || [];
      return {
        ...body,
        [RETURN_EXCHANGE_TYPE]: [...existingValues, ...mappedValues],
      };
    }

    // Special case for approvalType
    if (filterValue === APPROVAL_TYPES) {
      const mappedValues = Array.isArray(criterionArgumentValue)
        ? criterionArgumentValue.map((value) => APPROVAL_TYPE_MAPPING[value] || value)
        : [APPROVAL_TYPE_MAPPING[criterionArgumentValue] || criterionArgumentValue];
      const existingValues = body[APPROVAL_TYPES] || [];
      return {
        ...body,
        [APPROVAL_TYPES]: [...existingValues, ...mappedValues],
      };
    }

    // Special case for paymentType
    if (filterValue === REFUND_METHODS) {
      const mappedValues = Array.isArray(criterionArgumentValue)
        ? criterionArgumentValue.map((value) => PAYMENT_TYPE_MAPPING[value] || value)
        : [PAYMENT_TYPE_MAPPING[criterionArgumentValue] || criterionArgumentValue];
      const existingValues = body[REFUND_METHODS] || [];
      return {
        ...body,
        [REFUND_METHODS]: [...existingValues, ...mappedValues],
      };
    }

    // Special case for status
    if (filterValue === STATUS) {
      return {
        ...body,
        [STATUS]: criterionArgumentValue,
      };
    }

    // Special case for fulfillmentStatuses
    if (filterValue === FULFILLMENT_STATUSES) {
      const mappedValues = Array.isArray(criterionArgumentValue)
        ? criterionArgumentValue.map((value) => FULFILLMENT_STATUSES_MAPPING[value] || value)
        : [FULFILLMENT_STATUSES_MAPPING[criterionArgumentValue] || criterionArgumentValue];
      const existingValues = body[FULFILLMENT_STATUSES] || [];
      return {
        ...body,
        [FULFILLMENT_STATUSES]: [...existingValues, ...mappedValues],
      };
    }

    // Standard handling for non-date filters
    const valueAsArray = Array.isArray(criterionArgumentValue) ? criterionArgumentValue : [criterionArgumentValue];
    const existingValues = body[filterValue] || [];
    return {
      ...body,
      [filterValue]: [...existingValues, ...valueAsArray],
    };
  }, {});
};
